/**
 * usePaymentLinkActions — Iter 156.3 (Feb 27, 2026, Yash).
 *
 * Extracted from ChatsPage.jsx. Owns the 4 payment-link handlers
 * (create, copy, send-link, send-QR, download-QR) so the parent
 * page doesn't have to inline ~75 lines of payment logic.
 *
 * Pure handler hook — DOES NOT own any state. Receives the state
 * setters as arguments and calls them. Lets the parent keep state
 * close to the JSX while moving the algorithm out.
 */
import { paymentApi } from "../lib/api";
import { toast } from "sonner";

export const usePaymentLinkActions = ({
  // selection
  selectedLead,
  selectedChat,
  // form state
  paymentAmount,
  paymentDesc,
  paymentCurrency,
  paymentProvider,
  payCustomerName,
  payCustomerPhone,
  payCustomerEmail,
  // result state setters
  setCreatingPayment,
  setGeneratedLink,
  setQrBase64,
  setSendingQR,
  // composer + ui
  setReplyText,
  inputRef,
  setShowPaymentModal,
  setPaymentAmount,
  setPaymentDesc,
  generatedLink,
  qrBase64,
  loadMessages,
}) => {
  const handleCreatePaymentLink = async () => {
    if (!selectedLead || !paymentAmount) { toast.error("Enter amount"); return; }
    setCreatingPayment(true);
    try {
      const commonPayload = {
        lead_id: selectedLead.id,
        amount: parseFloat(paymentAmount),
        description: paymentDesc.trim() || "Payment",
        currency: paymentCurrency,
        customer_name: (payCustomerName || selectedLead.customer_name || selectedLead.name || "").trim(),
        customer_phone: (payCustomerPhone || selectedLead.customer_phone || selectedLead.phone || "").trim(),
        customer_email: (payCustomerEmail || selectedLead.customer_email || "").trim(),
      };
      let res;
      if (paymentProvider === "stripe") {
        // Stripe needs the frontend origin so the success/cancel URLs route
        // back to the CRM (per platform security rule — never hardcode).
        res = await paymentApi.createStripeLink({
          ...commonPayload,
          origin_url: window.location.origin,
        });
      } else if (paymentProvider === "paypal") {
        // PayPal — Orders v2 with intent=CAPTURE. Same origin trick so the
        // post-payment return/cancel routes back to the CRM.
        res = await paymentApi.createPaypalLink({
          ...commonPayload,
          origin_url: window.location.origin,
        });
      } else {
        res = await paymentApi.createLink(commonPayload);
      }
      const link = res.data.payment_link;
      setGeneratedLink(link);
      // Fetch QR code
      try {
        const qrRes = await paymentApi.getQR(link.id);
        setQrBase64(qrRes.data.qr_base64);
      } catch (e) { /* QR generation optional */ }
      toast.success("Payment link created!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create payment link");
    } finally { setCreatingPayment(false); }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const handleSendPaymentLink = (url) => {
    setReplyText(`Here's your payment link: ${url}`);
    setShowPaymentModal(false);
    setGeneratedLink(null);
    setPaymentAmount("");
    setPaymentDesc("");
    setQrBase64(null);
    inputRef.current?.focus();
  };

  const handleSendQR = async () => {
    if (!generatedLink) return;
    setSendingQR(true);
    try {
      const res = await paymentApi.sendQR(generatedLink.id);
      if (res.data.success) {
        toast.success("QR code sent via WhatsApp!");
        await loadMessages(selectedChat.id);
      } else {
        toast.error(res.data.error || "Failed to send QR");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send QR");
    } finally { setSendingQR(false); }
  };

  const handleDownloadQR = () => {
    if (!qrBase64) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${qrBase64}`;
    link.download = `payment-qr-${generatedLink?.amount || ""}.png`;
    link.click();
  };

  return {
    handleCreatePaymentLink,
    handleCopyLink,
    handleSendPaymentLink,
    handleSendQR,
    handleDownloadQR,
  };
};
